import React from "react";
import { ThemeProvider } from "styled-components";

import { FAVICON_CDN } from "@/config";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import { Interaction, InteractionResult } from "@/service/Model";
import ResizeService from "@/service/Resize";
import TrackingService from "@/service/Tracking";
import { GlobalStyles, themes } from "@/styles";

type AppProps = {
  themeKey: string;
  interaction: Interaction;
  interactionResults?: InteractionResult;
  onDestroy: () => void;
};

export type ReactComponent<P = {}> =
  | React.ComponentClass<P>
  | React.FunctionComponent<P>;

const App = <P extends {}>(WrappedComponent: ReactComponent) =>
  class extends React.Component<P & AppProps> {
    public componentDidMount(): void {
      const { interaction } = this.props;
      if (!interaction) {
        return;
      }
      // Track interaction initialization
      TrackingService.getInstance().interaction = interaction;

      // IFrameService initialization
      IFrameService.initialized();

      // Extract tealium via LA-Hyper
      IFrameService.getTealiumConfig();

      // Get canonical url
      IFrameService.getCanonical();

      ResizeService.init(this.onResize, 200);

      // Handles theme based favicon
      this.applyFavicon();

      // Set interaction title as document title
      document.title = interaction.title;
    }

    public render(): JSX.Element {
      const { themeKey, interaction } = this.props;

      // Consider in future useMemo or useEffect: Theme calculation is only needed if interaction or theme key changes
      const theme = themes[themeKey] || themes.default;
      if (this.useDynamicHeight(interaction)) {
        // Overwrite theme setting - content will stretch the height and la-hyper will resize the iframe
        theme.globals.minInteractionHeight = "0";
      }

      return (
        <ThemeProvider theme={theme}>
          <React.Fragment>
            <GlobalStyles fonts={themeKey} />
            <WrappedComponent {...this.props} />
          </React.Fragment>
        </ThemeProvider>
      );
    }

    /**
     * @func applyFavicon
     * Handles favicon based on theme
     */
    public applyFavicon(): void {
      const {
        themeKey,
        interaction: { type, layout },
      } = this.props;

      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      if (layout) {
        link.href = `${FAVICON_CDN}/${type}/${layout}/${themeKey}/favicon.ico`;
      } else {
        link.href = `${FAVICON_CDN}/${type}/${themeKey}/favicon.ico`;
      }

      document.getElementsByTagName("head")[0].appendChild(link);
    }

    public onResize(height: number): void {
      IFrameService.resize(height);
    }

    public useDynamicHeight(interaction: Interaction): boolean {
      return !InteractionService.hasBackgroundImage(interaction);
    }
  };

export default App;
